import React, { memo, useMemo, useState } from "react";
import { useAssets, extractAssetIds } from "../../hooks/useAssets";
import {
  TASKS_BOARD_ID,
  WEEKLY_CHECKS_BOARD_ID,
  METER_READINGS_BOARD_ID,
  CLEANING_CHECKS_BOARD_ID,
} from "../../constants/columnIds";
import { normalizeDateOnly, isDateMatch } from "../../utils/dateUtils";
import ProgressIndicator from "../common/ProgressIndicator";

const TaskPopup = memo(function TaskPopup({
  task,
  onClose,
  reportText,
  setReportText,
  reportImages,
  handleImageUpload,
  removeImage,
  handleSendReport,
  sendingReport,
  handleConfirmTask,
  confirmingTask,
  waterReading,
  setWaterReading,
  handleWaterPhotoUpload,
  removeWaterPhoto,
  gasReading,
  setGasReading,
  handleGasPhotoUpload,
  removeGasPhoto,
  electricityReading,
  setElectricityReading,
  readingDates,
  savingReading,
  handleSaveElectricityReading,
  handleSaveWaterReading,
  handleSaveGasReading,
  timeSpent,
  setTimeSpent,
  completedMeterReadingData,
  completedTaskData = { images: [], comments: "" },
  extras,
  setExtras,
  savingExtras,
  handleSaveExtras,
  isEditingTask,
  setIsEditingTask,
  editTaskName,
  setEditTaskName,
  editTaskDescription,
  setEditTaskDescription,
  editTaskDate,
  setEditTaskDate,
  editTaskPerson,
  setEditTaskPerson,
  editTaskTimeTracking,
  setEditTaskTimeTracking,
  savingTask,
  deletingTask,
  handleSaveTask,
  handleDeleteTask,
  availablePeople,
  propertyDetails = { keybox: "", alarm: "", doorGateCode: "", location: null },
  taskImages = [],
  setTaskImages,
  handleTaskPhotoUpload,
  removeTaskPhoto,
  taskComments = "",
  setTaskComments,
  imageProgress = { compressing: false, uploading: false, total: 0, completed: 0, currentFile: null },
  showConfirmedState = false,
  confirmationProgress = { uploading: false, total: 0, completed: 0, currentOperation: null },
}) {
  // Compute who the task is assigned to (for all boards)
  const assignedTo = useMemo(() => {
    if (task.personNames && task.personNames.length > 0) {
      return task.personNames.join(", ");
    }
    if (task.personText && String(task.personText).trim() !== "") {
      return String(task.personText).trim();
    }
    return "Unassigned";
  }, [task.personNames, task.personText]);

  // Memoize task type checks to avoid recalculation
  const taskTypes = useMemo(() => ({
    isMeterReadingTask: task.boardId === METER_READINGS_BOARD_ID,
    isWeeklyCheckTask: task.boardId === WEEKLY_CHECKS_BOARD_ID,
    isTasksBoardTask: task.boardId === TASKS_BOARD_ID,
    isCleaningChecksTask: task.boardId === CLEANING_CHECKS_BOARD_ID,
  }), [task.boardId]);

  const { isMeterReadingTask, isWeeklyCheckTask, isTasksBoardTask, isCleaningChecksTask } = taskTypes;

  // Check if time tracking is enabled (case-insensitive, handle various formats)
  const isTimeTrackingEnabled = useMemo(() => {
    if (!isTasksBoardTask || task.done) {
      return false;
    }
    
    return task.timeTracking && String(task.timeTracking).toLowerCase().trim() === "yes";
  }, [isTasksBoardTask, task.done, task.timeTracking]);

  // Extract asset IDs from completed meter reading data
  const waterAssetIds = useMemo(() => {
    if (completedMeterReadingData && completedMeterReadingData.water) {
      return extractAssetIds(completedMeterReadingData.water);
    }
    return [];
  }, [completedMeterReadingData]);

  const gasAssetIds = useMemo(() => {
    if (completedMeterReadingData && completedMeterReadingData.gas) {
      return extractAssetIds(completedMeterReadingData.gas);
    }
    return [];
  }, [completedMeterReadingData]);

  // Fetch assets using GraphQL
  const { 
    assets: waterAssets, 
    loading: waterAssetsLoading
  } = useAssets(waterAssetIds);

  const { 
    assets: gasAssets, 
    loading: gasAssetsLoading
  } = useAssets(gasAssetIds);

  // State for image modal
  const [viewingImage, setViewingImage] = useState(null);

  // Create a mapping of asset ID to asset data for quick lookup
  const waterAssetMap = useMemo(() => {
    const map = {};
    waterAssets.forEach(asset => {
      map[asset.id] = asset;
    });
    return map;
  }, [waterAssets]);

  const gasAssetMap = useMemo(() => {
    const map = {};
    gasAssets.forEach(asset => {
      map[asset.id] = asset;
    });
    return map;
  }, [gasAssets]);

  // Extract asset IDs from completed task images
  const taskAssetIds = useMemo(() => {
    if (completedTaskData && completedTaskData.images) {
      return extractAssetIds(completedTaskData.images);
    }
    return [];
  }, [completedTaskData]);

  // Fetch assets for task images using GraphQL
  const { 
    assets: taskAssets, 
    loading: taskAssetsLoading
  } = useAssets(taskAssetIds);

  // Create a mapping of asset ID to asset data for quick lookup
  const taskAssetMap = useMemo(() => {
    const map = {};
    taskAssets.forEach(asset => {
      map[asset.id] = asset;
    });
    return map;
  }, [taskAssets]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 8,
          padding: 24,
          maxWidth: 500,
          width: "90%",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* PHASE 2: Show confirmation state overlay */}
        {showConfirmedState && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 200, 117, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                fontSize: 32,
                fontWeight: "bold",
                color: "#00c875",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span>✓</span>
              <span>Confirmed!</span>
            </div>
          </div>
        )}
        
        {/* PHASE 3: Show progress indicator for Meter Readings */}
        {confirmationProgress.uploading && confirmationProgress.total > 0 && isMeterReadingTask && (
          <div
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              backgroundColor: "#00c875",
              color: "#ffffff",
              padding: "8px 16px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 500,
              zIndex: 20,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            {confirmationProgress.currentOperation && (
              <div style={{ marginBottom: 4 }}>{confirmationProgress.currentOperation}</div>
            )}
            <div>
              {confirmationProgress.completed} / {confirmationProgress.total} uploaded
            </div>
          </div>
        )}
        
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 8 }}>
            {/* Show clock icon for tasks with time tracking enabled */}
            {isTasksBoardTask && task.timeTracking && String(task.timeTracking).toLowerCase().trim() === "yes" && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                  color: "#323338",
                }}
                title="Time tracking enabled"
              >
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
              </svg>
            )}
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
              {task.name}
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                fontSize: 24,
                cursor: "pointer",
                color: "#676879",
                padding: 0,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Close"
            >
              ×
            </button>
            <div style={{ fontSize: 12, color: "#676879", textAlign: "right" }}>
              <span style={{ fontWeight: 500 }}>Assigned to:</span>{" "}
              <span>{assignedTo}</span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: "#676879", fontSize: 13 }}>Property:</strong>
            <div style={{ marginTop: 4, fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{task.propertyName}</span>
              {propertyDetails.location && (
                <button
                  onClick={() => {
                    const { lat, lng } = propertyDetails.location;
                    // Open Google Maps with coordinates
                    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 4,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#323338",
                    width: 20,
                    height: 20,
                  }}
                  title="Open in Google Maps"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: "block" }}
                  >
                    <path
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              )}
            </div>
            {(propertyDetails.keybox || propertyDetails.alarm || propertyDetails.doorGateCode) && (
              <div style={{ marginTop: 6, fontSize: 13, color: "#676879" }}>
                {propertyDetails.keybox && (
                  <span style={{ marginRight: 12 }}>Keybox: {propertyDetails.keybox}</span>
                )}
                {propertyDetails.alarm && (
                  <span style={{ marginRight: 12 }}>Alarm: {propertyDetails.alarm}</span>
                )}
                {propertyDetails.doorGateCode && (
                  <span>Door/Gate Code: {propertyDetails.doorGateCode}</span>
                )}
              </div>
            )}
          </div>
          {isTasksBoardTask && task.description && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: "#676879", fontSize: 13 }}>Description:</strong>
              <div style={{ marginTop: 4, fontSize: 14, color: "#323338", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {task.description}
              </div>
            </div>
          )}
        </div>

        {isMeterReadingTask ? (
          <div
            style={{
              paddingTop: 16,
              borderTop: "1px solid #e6e9ef",
              marginBottom: 20,
            }}
          >
            {/* Show Notes if available - placed above Meter Readings heading */}
            {(task.notes || completedMeterReadingData.notes) && (
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    color: "#676879",
                    marginBottom: 6,
                    fontWeight: 500,
                  }}
                >
                  Notes:
                </label>
                <div
                  style={{
                    padding: 8,
                    border: "1px solid #e6e9ef",
                    borderRadius: 4,
                    fontSize: 14,
                    backgroundColor: "#f4f5f8",
                    color: "#323338",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {task.notes || completedMeterReadingData.notes}
                </div>
              </div>
            )}

            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
              Meter Readings
            </h3>

            {/* Show completed meter reading data if task is completed */}
            {task.isCompletedEntry && completedMeterReadingData ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Electricity:
                  </label>
                  <div
                    style={{
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      backgroundColor: "#f4f5f8",
                      color: "#323338",
                      minHeight: 20,
                    }}
                  >
                    {completedMeterReadingData.electricity || "No reading recorded"}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Water Meter Photos:
                  </label>
                  {completedMeterReadingData.water && completedMeterReadingData.water.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {completedMeterReadingData.water.map((file, index) => {
                        // Get the asset data from GraphQL query if available
                        const asset = file.assetId ? waterAssetMap[file.assetId] : null;
                        const imageUrl = asset ? asset.public_url : file.url;
                        
                        return (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              width: 120,
                              height: 120,
                              borderRadius: 4,
                              overflow: "hidden",
                              border: "1px solid #e6e9ef",
                              cursor: "pointer",
                            }}
                            onClick={() => setViewingImage({ url: imageUrl, name: file.name || `Water meter ${index + 1}` })}
                          >
                            {waterAssetsLoading ? (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f4f5f8",
                                  color: "#676879",
                                  fontSize: 12,
                                }}
                              >
                                Loading...
                              </div>
                            ) : (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={file.name || `Water meter ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                                <div
                                  style={{
                                    display: "none",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f4f5f8",
                                    color: "#676879",
                                    fontSize: 12,
                                    textAlign: "center",
                                    padding: 8,
                                  }}
                                >
                                  {file.name || "Image"}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        backgroundColor: "#f4f5f8",
                        color: "#676879",
                      }}
                    >
                      No photos recorded
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Gas Meter Photos:
                  </label>
                  {completedMeterReadingData.gas && completedMeterReadingData.gas.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {completedMeterReadingData.gas.map((file, index) => {
                        // Get the asset data from GraphQL query if available
                        const asset = file.assetId ? gasAssetMap[file.assetId] : null;
                        const imageUrl = asset ? asset.public_url : file.url;
                        
                        return (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              width: 120,
                              height: 120,
                              borderRadius: 4,
                              overflow: "hidden",
                              border: "1px solid #e6e9ef",
                              cursor: "pointer",
                            }}
                            onClick={() => setViewingImage({ url: imageUrl, name: file.name || `Water meter ${index + 1}` })}
                          >
                            {gasAssetsLoading ? (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f4f5f8",
                                  color: "#676879",
                                  fontSize: 12,
                                }}
                              >
                                Loading...
                              </div>
                            ) : (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={file.name || `Gas meter ${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                                <div
                                  style={{
                                    display: "none",
                                    width: "100%",
                                    height: "100%",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f4f5f8",
                                    color: "#676879",
                                    fontSize: 12,
                                    textAlign: "center",
                                    padding: 8,
                                  }}
                                >
                                  {file.name || "Image"}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        backgroundColor: "#f4f5f8",
                        color: "#676879",
                      }}
                    >
                      No photos recorded
                    </div>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={onClose}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      background: "#ffffff",
                      color: "#323338",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Show input form for incomplete tasks */}
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Electricity:
                  </label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      value={electricityReading}
                      onChange={(e) => setElectricityReading(e.target.value)}
                      placeholder="Enter electricity reading..."
                      style={{
                        flex: 1,
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                    />
                    {(() => {
                      if (!readingDates || !readingDates.electricity) return false;
                      const today = normalizeDateOnly(new Date());
                      // readingDates.electricity should already be a normalized Date object
                      const readingDate = readingDates.electricity instanceof Date 
                        ? normalizeDateOnly(readingDates.electricity)
                        : normalizeDateOnly(readingDates.electricity);
                      return today && readingDate && today.getTime() === readingDate.getTime();
                    })() ? (
                      <span style={{ color: "#00c875", fontSize: 20, fontWeight: "bold", minWidth: 24, textAlign: "center" }}>✓</span>
                    ) : (
                      <button
                        onClick={handleSaveElectricityReading}
                        disabled={!electricityReading.trim() || savingReading?.electricity}
                        style={{
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: 4,
                          background: electricityReading.trim() && !savingReading?.electricity ? "#00c875" : "#c4c4c4",
                          color: "#ffffff",
                          cursor: electricityReading.trim() && !savingReading?.electricity ? "pointer" : "not-allowed",
                          fontSize: 13,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "background-color 0.2s",
                        }}
                      >
                        {savingReading?.electricity ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Water Meter Photo:
                  </label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleWaterPhotoUpload}
                      style={{
                        flex: 1,
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    />
                    {(() => {
                      if (!readingDates || !readingDates.water) return false;
                      const today = normalizeDateOnly(new Date());
                      // readingDates.water should already be a normalized Date object
                      const readingDate = readingDates.water instanceof Date 
                        ? normalizeDateOnly(readingDates.water)
                        : normalizeDateOnly(readingDates.water);
                      return today && readingDate && today.getTime() === readingDate.getTime();
                    })() ? (
                      <span style={{ color: "#00c875", fontSize: 20, fontWeight: "bold", minWidth: 24, textAlign: "center" }}>✓</span>
                    ) : (
                      <button
                        onClick={handleSaveWaterReading}
                        disabled={waterReading.length === 0 || savingReading?.water}
                        style={{
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: 4,
                          background: waterReading.length > 0 && !savingReading?.water ? "#00c875" : "#c4c4c4",
                          color: "#ffffff",
                          cursor: waterReading.length > 0 && !savingReading?.water ? "pointer" : "not-allowed",
                          fontSize: 13,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "background-color 0.2s",
                        }}
                      >
                        {savingReading?.water ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                  
                  {/* Show saved water images if date is today, or newly uploaded images */}
                  {(() => {
                    if (!readingDates || !readingDates.water) {
                      return waterReading.length > 0 ? (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {waterReading.map((image, index) => (
                            <div key={index} style={{ position: "relative", width: 80, height: 80, borderRadius: 4, overflow: "hidden", border: "1px solid #e6e9ef" }}>
                              <img src={URL.createObjectURL(image)} alt={`Water meter ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              <button onClick={() => removeWaterPhoto(index)} style={{ position: "absolute", top: 4, right: 4, background: "rgba(0, 0, 0, 0.7)", color: "#ffffff", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    }
                    const today = normalizeDateOnly(new Date());
                    const readingDate = readingDates.water instanceof Date 
                      ? normalizeDateOnly(readingDates.water)
                      : normalizeDateOnly(readingDates.water);
                    const isToday = today && readingDate && today.getTime() === readingDate.getTime();
                    const imagesToShow = isToday && completedMeterReadingData?.water && completedMeterReadingData.water.length > 0
                      ? completedMeterReadingData.water
                      : waterReading;
                    
                    return imagesToShow.length > 0 ? (
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {imagesToShow.map((image, index) => {
                          const imageUrl = image instanceof File 
                            ? URL.createObjectURL(image)
                            : image.url || image;
                          const isNewImage = image instanceof File;
                          
                          return (
                            <div
                              key={index}
                              style={{
                                position: "relative",
                                width: 80,
                                height: 80,
                                borderRadius: 4,
                                overflow: "hidden",
                                border: "1px solid #e6e9ef",
                              }}
                            >
                              <img
                                src={imageUrl}
                                alt={`Water meter ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              {isNewImage && (
                                <button
                                  onClick={() => removeWaterPhoto(index)}
                                  style={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    background: "rgba(0, 0, 0, 0.7)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: 20,
                                    height: 20,
                                    cursor: "pointer",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null;
                  })()}
                  <ProgressIndicator progress={imageProgress} />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Gas Meter Photo:
                  </label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGasPhotoUpload}
                      style={{
                        flex: 1,
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    />
                    {(() => {
                      if (!readingDates || !readingDates.gas) return false;
                      const today = normalizeDateOnly(new Date());
                      // readingDates.gas should already be a normalized Date object
                      const readingDate = readingDates.gas instanceof Date 
                        ? normalizeDateOnly(readingDates.gas)
                        : normalizeDateOnly(readingDates.gas);
                      return today && readingDate && today.getTime() === readingDate.getTime();
                    })() ? (
                      <span style={{ color: "#00c875", fontSize: 20, fontWeight: "bold", minWidth: 24, textAlign: "center" }}>✓</span>
                    ) : (
                      <button
                        onClick={handleSaveGasReading}
                        disabled={gasReading.length === 0 || savingReading?.gas}
                        style={{
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: 4,
                          background: gasReading.length > 0 && !savingReading?.gas ? "#00c875" : "#c4c4c4",
                          color: "#ffffff",
                          cursor: gasReading.length > 0 && !savingReading?.gas ? "pointer" : "not-allowed",
                          fontSize: 13,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          transition: "background-color 0.2s",
                        }}
                      >
                        {savingReading?.gas ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                  
                  {/* Show saved gas images if date is today, or newly uploaded images */}
                  {(() => {
                    if (!readingDates || !readingDates.gas) {
                      return gasReading.length > 0 ? (
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {gasReading.map((image, index) => (
                            <div key={index} style={{ position: "relative", width: 80, height: 80, borderRadius: 4, overflow: "hidden", border: "1px solid #e6e9ef" }}>
                              <img src={URL.createObjectURL(image)} alt={`Gas meter ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              <button onClick={() => removeGasPhoto(index)} style={{ position: "absolute", top: 4, right: 4, background: "rgba(0, 0, 0, 0.7)", color: "#ffffff", border: "none", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    }
                    const today = normalizeDateOnly(new Date());
                    const readingDate = readingDates.gas instanceof Date 
                      ? normalizeDateOnly(readingDates.gas)
                      : normalizeDateOnly(readingDates.gas);
                    const isToday = today && readingDate && today.getTime() === readingDate.getTime();
                    const imagesToShow = isToday && completedMeterReadingData?.gas && completedMeterReadingData.gas.length > 0
                      ? completedMeterReadingData.gas
                      : gasReading;
                    
                    return imagesToShow.length > 0 ? (
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {imagesToShow.map((image, index) => {
                          const imageUrl = image instanceof File 
                            ? URL.createObjectURL(image)
                            : image.url || image;
                          const isNewImage = image instanceof File;
                          
                          return (
                            <div
                              key={index}
                              style={{
                                position: "relative",
                                width: 80,
                                height: 80,
                                borderRadius: 4,
                                overflow: "hidden",
                                border: "1px solid #e6e9ef",
                              }}
                            >
                              <img
                                src={imageUrl}
                                alt={`Gas meter ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              {isNewImage && (
                                <button
                                  onClick={() => removeGasPhoto(index)}
                                  style={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    background: "rgba(0, 0, 0, 0.7)",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: 20,
                                    height: 20,
                                    cursor: "pointer",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : null;
                  })()}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={onClose}
                    disabled={confirmingTask}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      background: "#ffffff",
                      color: "#323338",
                      cursor: confirmingTask ? "not-allowed" : "pointer",
                      fontSize: 14,
                      opacity: confirmingTask ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </button>
                  {!task.done && (
                    <button
                      onClick={handleConfirmTask}
                      disabled={confirmingTask}
                      style={{
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: 4,
                        background: confirmingTask ? "#a1a4b5" : "#00c875",
                        color: "#ffffff",
                        cursor: confirmingTask ? "wait" : "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                        transform: confirmingTask ? "scale(0.98)" : "scale(1)",
                        opacity: confirmingTask ? 0.8 : 1,
                      }}
                    >
                      {confirmingTask ? "Confirming..." : "Confirm Task"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : isWeeklyCheckTask ? (
          <div
            style={{
              paddingTop: 16,
              borderTop: "1px solid #e6e9ef",
              marginBottom: 20,
            }}
          >
            {/* Display subitems if they exist */}
            {task.subitems && task.subitems.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                  Extra Tasks
                </h3>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {task.subitems.map((subitem) => (
                    <li
                      key={subitem.id}
                      style={{
                        padding: "8px 12px",
                        background: "#f4f5f8",
                        borderRadius: 4,
                        fontSize: 14,
                        color: "#323338",
                      }}
                    >
                      {subitem.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
              Report
            </h3>
            
            <div style={{ marginBottom: 12 }}>
              <textarea
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder="Enter your report..."
                style={{
                  width: "100%",
                  minHeight: 60,
                  padding: 8,
                  border: "1px solid #e6e9ef",
                  borderRadius: 4,
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#676879",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                Upload Images:
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                style={{
                  width: "100%",
                  padding: 8,
                  border: "1px solid #e6e9ef",
                  borderRadius: 4,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              />
              
              {reportImages.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {reportImages.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        width: 80,
                        height: 80,
                        borderRadius: 4,
                        overflow: "hidden",
                        border: "1px solid #e6e9ef",
                      }}
                    >
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "rgba(0, 0, 0, 0.7)",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "50%",
                          width: 20,
                          height: 20,
                          cursor: "pointer",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={handleSendReport}
                  disabled={sendingReport || (!reportText.trim() && reportImages.length === 0)}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 4,
                    background: sendingReport || (!reportText.trim() && reportImages.length === 0) ? "#a1a4b5" : "#0073ea",
                    color: "#ffffff",
                    cursor: sendingReport || (!reportText.trim() && reportImages.length === 0) ? "not-allowed" : "pointer",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {sendingReport ? "Sending..." : "Send Report"}
                </button>
              </div>
              
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={onClose}
                  disabled={sendingReport || confirmingTask}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #e6e9ef",
                    borderRadius: 4,
                    background: "#ffffff",
                    color: "#323338",
                    cursor: (sendingReport || confirmingTask) ? "not-allowed" : "pointer",
                    fontSize: 14,
                    opacity: (sendingReport || confirmingTask) ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                {!task.done && (
                  <button
                    onClick={handleConfirmTask}
                    disabled={sendingReport || confirmingTask}
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: 4,
                      background: (sendingReport || confirmingTask) ? "#a1a4b5" : "#00c875",
                      color: "#ffffff",
                      cursor: (sendingReport || confirmingTask) ? "wait" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      transform: (sendingReport || confirmingTask) ? "scale(0.98)" : "scale(1)",
                      opacity: (sendingReport || confirmingTask) ? 0.8 : 1,
                    }}
                  >
                    {confirmingTask ? "Confirming..." : "Confirm Task"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : isCleaningChecksTask ? (
          <div
            style={{
              paddingTop: 16,
              borderTop: "1px solid #e6e9ef",
              marginBottom: 20,
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
              Cleaning Check Details
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#676879",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                Notes:
              </label>
              <textarea
                value={extras !== undefined ? extras : (task.extras || "")}
                onChange={(e) => setExtras(e.target.value)}
                placeholder="Enter notes for this cleaning check..."
                style={{
                  width: "100%",
                  minHeight: 80,
                  padding: 8,
                  border: "1px solid #e6e9ef",
                  borderRadius: 4,
                  fontSize: 14,
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleSaveExtras}
                disabled={savingExtras}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: 4,
                  background: savingExtras ? "#a1a4b5" : "#0073ea",
                  color: "#ffffff",
                  cursor: savingExtras ? "not-allowed" : "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {savingExtras ? "Saving..." : "Save Notes"}
              </button>
              
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={onClose}
                  disabled={savingExtras || confirmingTask}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #e6e9ef",
                    borderRadius: 4,
                    background: "#ffffff",
                    color: "#323338",
                    cursor: (savingExtras || confirmingTask) ? "not-allowed" : "pointer",
                    fontSize: 14,
                    opacity: (savingExtras || confirmingTask) ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                {!task.done && (
                  <button
                    onClick={handleConfirmTask}
                    disabled={savingExtras || confirmingTask}
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: 4,
                      background: (savingExtras || confirmingTask) ? "#a1a4b5" : "#00c875",
                      color: "#ffffff",
                      cursor: (savingExtras || confirmingTask) ? "wait" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      transform: (savingExtras || confirmingTask) ? "scale(0.98)" : "scale(1)",
                      opacity: (savingExtras || confirmingTask) ? 0.8 : 1,
                    }}
                  >
                    {confirmingTask ? "Confirming..." : "Confirm Task"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : isTasksBoardTask ? (
          <div
            style={{
              paddingTop: 16,
              borderTop: "1px solid #e6e9ef",
              marginBottom: 20,
            }}
          >
            {/* Edit mode for TASKS */}
            {isEditingTask ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Task Name:
                  </label>
                  <input
                    type="text"
                    value={editTaskName}
                    onChange={(e) => setEditTaskName(e.target.value)}
                    placeholder="Enter task name..."
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Description:
                  </label>
                  <textarea
                    value={editTaskDescription}
                    onChange={(e) => setEditTaskDescription(e.target.value)}
                    placeholder="Enter task description..."
                    style={{
                      width: "100%",
                      minHeight: 80,
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: "inherit",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Due Date:
                  </label>
                  <input
                    type="date"
                    value={editTaskDate}
                    onChange={(e) => setEditTaskDate(e.target.value)}
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Person:
                  </label>
                  <select
                    value={editTaskPerson}
                    onChange={(e) => setEditTaskPerson(e.target.value)}
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      background: "#ffffff",
                    }}
                  >
                    <option value="">No person assigned</option>
                    {availablePeople && availablePeople.map((person) => (
                      <option key={person.id} value={person.name || person.id}>
                        {person.name || person.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={editTaskTimeTracking}
                      onChange={(e) => setEditTaskTimeTracking(e.target.checked)}
                      style={{
                        marginRight: 8,
                        cursor: "pointer",
                      }}
                    />
                    Time Tracking Required
                  </label>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={() => setIsEditingTask(false)}
                    disabled={savingTask}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      background: "#ffffff",
                      color: "#323338",
                      cursor: savingTask ? "not-allowed" : "pointer",
                      fontSize: 14,
                      opacity: savingTask ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTask}
                    disabled={savingTask || !editTaskName.trim()}
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: 4,
                      background: savingTask || !editTaskName.trim() ? "#a1a4b5" : "#0073ea",
                      color: "#ffffff",
                      cursor: savingTask || !editTaskName.trim() ? "not-allowed" : "pointer",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {savingTask ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Show completed task data if task is completed */}
                {(task.done || task.isCompletedEntry) && completedTaskData && (
                  <>
                    {/* Show completed task images */}
                    {completedTaskData.images && completedTaskData.images.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            color: "#676879",
                            marginBottom: 6,
                            fontWeight: 500,
                          }}
                        >
                          Images:
                        </label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {completedTaskData.images.map((file, index) => {
                            // Get the asset data from GraphQL query if available
                            const asset = file.assetId ? taskAssetMap[file.assetId] : null;
                            const imageUrl = asset ? asset.public_url : file.url;
                            
                            return (
                              <div
                                key={index}
                                style={{
                                  position: "relative",
                                  width: 120,
                                  height: 120,
                                  borderRadius: 4,
                                  overflow: "hidden",
                                  border: "1px solid #e6e9ef",
                                  cursor: "pointer",
                                }}
                                onClick={() => setViewingImage({ url: imageUrl, name: file.name || `Task image ${index + 1}` })}
                              >
                                {taskAssetsLoading ? (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "#f4f5f8",
                                      color: "#676879",
                                      fontSize: 12,
                                    }}
                                  >
                                    Loading...
                                  </div>
                                ) : (
                                  <>
                                    <img
                                      src={imageUrl}
                                      alt={file.name || `Task image ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display = "flex";
                                      }}
                                    />
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "none",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#f4f5f8",
                                        color: "#676879",
                                        fontSize: 12,
                                      }}
                                    >
                                      Failed to load
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Show completed task comments */}
                    {completedTaskData.comments && (
                      <div style={{ marginBottom: 16 }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            color: "#676879",
                            marginBottom: 6,
                            fontWeight: 500,
                          }}
                        >
                          Comments:
                        </label>
                        <div
                          style={{
                            padding: 8,
                            border: "1px solid #e6e9ef",
                            borderRadius: 4,
                            fontSize: 14,
                            backgroundColor: "#f4f5f8",
                            color: "#323338",
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.5,
                          }}
                        >
                          {completedTaskData.comments}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Show time spent input for TASKS board if Time Tracking is enabled */}
                {!task.done && isTimeTrackingEnabled && (
                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        color: "#676879",
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      Time Spent (minutes) <span style={{ color: "#d83a52" }}>*</span>
                    </label>
                    <input
                      type="number"
                      value={timeSpent}
                      onChange={(e) => setTimeSpent(e.target.value)}
                      placeholder="Enter time in minutes..."
                      min="0"
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        fontSize: 14,
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                )}

                {/* Image upload section for TASKS (only show if not completed) */}
                {!task.done && (
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Images:
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleTaskPhotoUpload}
                    style={{
                      width: "100%",
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  />
                  {taskImages.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {taskImages.map((image, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            width: 80,
                            height: 80,
                            borderRadius: 4,
                            overflow: "hidden",
                            border: "1px solid #e6e9ef",
                          }}
                        >
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`Task image ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            onClick={() => removeTaskPhoto(index)}
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              background: "rgba(0, 0, 0, 0.7)",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              cursor: "pointer",
                              fontSize: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Comments section for TASKS (only show if not completed) */}
                {!task.done && (
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      color: "#676879",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Comments:
                  </label>
                  <textarea
                    value={taskComments}
                    onChange={(e) => setTaskComments(e.target.value)}
                    placeholder="Add comments when completing this task..."
                    style={{
                      width: "100%",
                      minHeight: 80,
                      padding: 8,
                      border: "1px solid #e6e9ef",
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: "inherit",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      onClick={() => setIsEditingTask(true)}
                      disabled={savingTask || deletingTask || confirmingTask}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        background: "#ffffff",
                        color: "#323338",
                        cursor: (savingTask || deletingTask || confirmingTask) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        opacity: (savingTask || deletingTask || confirmingTask) ? 0.6 : 1,
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={handleDeleteTask}
                      disabled={savingTask || deletingTask || confirmingTask}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        background: "#ffffff",
                        color: "#d83a52",
                        cursor: (savingTask || deletingTask || confirmingTask) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        opacity: (savingTask || deletingTask || confirmingTask) ? 0.6 : 1,
                      }}
                    >
                      {deletingTask ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                  
                  <div style={{ display: "flex", gap: 12 }}>
                    <button
                      onClick={onClose}
                      disabled={confirmingTask || savingTask || deletingTask}
                      style={{
                        padding: "8px 16px",
                        border: "1px solid #e6e9ef",
                        borderRadius: 4,
                        background: "#ffffff",
                        color: "#323338",
                        cursor: (confirmingTask || savingTask || deletingTask) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        opacity: (confirmingTask || savingTask || deletingTask) ? 0.6 : 1,
                      }}
                    >
                      Cancel
                    </button>
                    {!task.done && (
                      <button
                        onClick={handleConfirmTask}
                        disabled={
                          confirmingTask ||
                          savingTask ||
                          deletingTask ||
                          (isTimeTrackingEnabled && (!timeSpent || timeSpent.trim() === "" || isNaN(parseInt(timeSpent.trim()))))
                        }
                        style={{
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: 4,
                          background: 
                            confirmingTask ||
                            savingTask ||
                            deletingTask ||
                            (isTimeTrackingEnabled && (!timeSpent || timeSpent.trim() === "" || isNaN(parseInt(timeSpent.trim()))))
                              ? "#a1a4b5"
                              : "#00c875",
                          color: "#ffffff",
                          cursor: 
                            confirmingTask ||
                            savingTask ||
                            deletingTask ||
                            (isTimeTrackingEnabled && (!timeSpent || timeSpent.trim() === "" || isNaN(parseInt(timeSpent.trim()))))
                              ? "not-allowed"
                              : "pointer",
                          fontSize: 14,
                          fontWeight: 600,
                          transition: "all 0.2s ease",
                          transform: confirmingTask ? "scale(0.98)" : "scale(1)",
                          opacity: confirmingTask ? 0.8 : 1,
                        }}
                      >
                        {confirmingTask ? "Confirming..." : "Confirm Task"}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            style={{
              paddingTop: 16,
              borderTop: "1px solid #e6e9ef",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                onClick={onClose}
                disabled={confirmingTask}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #e6e9ef",
                  borderRadius: 4,
                  background: "#ffffff",
                  color: "#323338",
                  cursor: confirmingTask ? "not-allowed" : "pointer",
                  fontSize: 14,
                  opacity: confirmingTask ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              {!task.done && (
                <button
                  onClick={handleConfirmTask}
                  disabled={confirmingTask}
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: 4,
                    background: confirmingTask ? "#a1a4b5" : "#00c875",
                    color: "#ffffff",
                    cursor: confirmingTask ? "not-allowed" : "pointer",
                    fontSize: 14,
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    transform: confirmingTask ? "scale(0.98)" : "scale(1)",
                    opacity: confirmingTask ? 0.8 : 1,
                  }}
                >
                  {confirmingTask ? "Confirming..." : "Confirm Task"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {viewingImage && (
        <div
          onClick={() => setViewingImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => setViewingImage(null)}
              style={{
                position: "absolute",
                top: "-40px",
                right: 0,
                background: "rgba(255, 255, 255, 0.9)",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "20px",
                color: "#323338",
                fontWeight: "bold",
                zIndex: 10001,
              }}
              title="Close"
            >
              ×
            </button>
            <img
              src={viewingImage.url}
              alt={viewingImage.name}
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
            {viewingImage.name && (
              <div
                style={{
                  marginTop: "12px",
                  color: "#ffffff",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {viewingImage.name}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default TaskPopup;
